/**
 * /tools/weekly-review?clientId=X — STP-042.
 *
 * The pilot's most-used cadence tool. 3 wins + good/notGood/needsImprovement
 * + nextWeekFocus + per-axis (energy/work/love) commitments. Persists as
 * versioned `client_documents` doc_type=`weekly_review`.
 *
 * The `nextWeekFocus` field is the cross-flow point — `/tools/carpe-diem`
 * (R1.3) reads it on render and surfaces it as a contextual banner above
 * the matrix (closes WFL-122).
 */

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import * as api from '@/lib/api-client';
import { useClientId } from '@/lib/use-client-id';
import { ApiError } from '@/lib/api-client';
import { FinalizeWarning } from '@/components/finalize-warning';
import { PrintButton } from '@/components/print-button';

interface FormState {
  weekLabel: string;
  wins: string[];
  good: string;
  notGood: string;
  needsImprovement: string;
  nextWeekFocus: string;
  energyCommitment: string;
  workCommitment: string;
  loveCommitment: string;
  rewardEarned: string;
}

const DEFAULT_FORM: FormState = {
  weekLabel: '',
  wins: ['', '', ''],
  good: '',
  notGood: '',
  needsImprovement: '',
  nextWeekFocus: '',
  energyCommitment: '',
  workCommitment: '',
  loveCommitment: '',
  rewardEarned: '',
};

export function WeeklyReviewPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken } = useAuth();
  const clientId = useClientId();

  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [hydrated, setHydrated] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    if (!clientId) {
      setHydrated(true);
      return;
    }
    void (async () => {
      try {
        const existing = await api.getToolCompletion(accessToken, clientId, 'weekly-review');
        if (!active) return;
        if (existing?.payload && typeof existing.payload === 'object') {
          const p = existing.payload as Record<string, unknown>;
          const next: FormState = { ...DEFAULT_FORM };
          if (typeof p.weekLabel === 'string') next.weekLabel = p.weekLabel;
          if (Array.isArray(p.wins) && p.wins.length > 0) {
            const wins = p.wins.map((raw) => {
              const w = raw as Record<string, unknown>;
              return typeof w.text === 'string' ? w.text : '';
            });
            next.wins = [wins[0] ?? '', wins[1] ?? '', wins[2] ?? ''];
          }
          if (typeof p.good === 'string') next.good = p.good;
          if (typeof p.notGood === 'string') next.notGood = p.notGood;
          if (typeof p.needsImprovement === 'string') next.needsImprovement = p.needsImprovement;
          if (typeof p.nextWeekFocus === 'string') next.nextWeekFocus = p.nextWeekFocus;
          if (p.energyCommitment && typeof p.energyCommitment === 'object') {
            const c = (p.energyCommitment as Record<string, unknown>).text;
            if (typeof c === 'string') next.energyCommitment = c;
          }
          if (p.workCommitment && typeof p.workCommitment === 'object') {
            const c = (p.workCommitment as Record<string, unknown>).text;
            if (typeof c === 'string') next.workCommitment = c;
          }
          if (p.loveCommitment && typeof p.loveCommitment === 'object') {
            const c = (p.loveCommitment as Record<string, unknown>).text;
            if (typeof c === 'string') next.loveCommitment = c;
          }
          if (typeof p.rewardEarned === 'string') next.rewardEarned = p.rewardEarned;
          setForm(next);
          setSavedAt(existing.completed_at ?? null);
        }
      } catch {
        /* swallow */
      } finally {
        if (active) setHydrated(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId]);

  const winTexts = form.wins.filter((w) => w.trim().length > 0);
  const canSubmit =
    !submitting &&
    hydrated &&
    winTexts.length >= 1 &&
    form.nextWeekFocus.trim().length > 0;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
    event.preventDefault();
    setSubmitError(null);
    if (!clientId) {
      setSubmitError(t('tools.weeklyReview.errors.missingClient'));
      return;
    }
    setSubmitting(true);
    const payload = {
      ...(form.weekLabel.trim() ? { weekLabel: form.weekLabel.trim() } : {}),
      wins: winTexts.map((text) => ({ text: text.trim() })),
      ...(form.good.trim() ? { good: form.good.trim() } : {}),
      ...(form.notGood.trim() ? { notGood: form.notGood.trim() } : {}),
      ...(form.needsImprovement.trim()
        ? { needsImprovement: form.needsImprovement.trim() }
        : {}),
      nextWeekFocus: form.nextWeekFocus.trim(),
      ...(form.energyCommitment.trim()
        ? { energyCommitment: { text: form.energyCommitment.trim() } }
        : {}),
      ...(form.workCommitment.trim()
        ? { workCommitment: { text: form.workCommitment.trim() } }
        : {}),
      ...(form.loveCommitment.trim()
        ? { loveCommitment: { text: form.loveCommitment.trim() } }
        : {}),
      ...(form.rewardEarned.trim() ? { rewardEarned: form.rewardEarned.trim() } : {}),
    };
    try {
      const completion = await api.submitToolCompletion(
        accessToken,
        clientId,
        'weekly-review',
        { payload },
      );
      setSavedAt(completion.completed_at ?? new Date().toISOString());
    } catch (cause) {
      if (cause instanceof ApiError) {
        setSubmitError(cause.envelope.message || t('tools.weeklyReview.errors.generic'));
      } else {
        setSubmitError(t('tools.weeklyReview.errors.generic'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!clientId) {
    return (
      <section className="max-w-2xl mx-auto px-s5 py-s8" data-testid="weekly-review-missing-client">
        <h1 className="font-display text-display-lg text-ink">{t('tools.weeklyReview.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('tools.weeklyReview.errors.missingClient')}</p>
      </section>
    );
  }

  const setWin = (idx: number, value: string): void => {
    setForm((prev) => ({
      ...prev,
      wins: prev.wins.map((w, i) => (i === idx ? value : w)),
    }));
  };

  return (
    <section className="max-w-3xl mx-auto px-s5 py-s7">
      <div className="flex justify-end mb-s2">
        <PrintButton />
      </div>
      <header className="mb-s6">
        <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
          {t('tools.weeklyReview.eyebrow')}
        </p>
        <h1 className="font-display text-display-xl text-ink mt-s2">
          {t('tools.weeklyReview.title')}
        </h1>
        <p className="mt-s3 text-gray-2 max-w-prose">{t('tools.weeklyReview.description')}</p>
      </header>

      <form
        onSubmit={(event) => {
          void onSubmit(event);
        }}
        className="space-y-s5"
        noValidate
      >
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">{t('tools.weeklyReview.fields.weekLabel')}</span>
            <input
              type="text"
              value={form.weekLabel}
              onChange={(e) => setForm((prev) => ({ ...prev, weekLabel: e.target.value }))}
              placeholder={t('tools.weeklyReview.placeholders.weekLabel')}
              className="input-field mt-s2 w-full"
              data-testid="weekly-review-week-label"
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.weeklyReview.wins.title')}
          </h2>
          <p className="mt-s2 text-gray-2 italic">{t('tools.weeklyReview.wins.hint')}</p>
          <ol className="mt-s3 space-y-s2">
            {[0, 1, 2].map((idx) => (
              <li key={idx}>
                <textarea
                  value={form.wins[idx] ?? ''}
                  onChange={(e) => setWin(idx, e.target.value)}
                  placeholder={t('tools.weeklyReview.placeholders.win', { n: idx + 1 })}
                  className="input-field min-h-16 w-full"
                  data-testid={`weekly-review-win-${idx}`}
                  rows={2}
                />
              </li>
            ))}
          </ol>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3">
          {(['good', 'notGood', 'needsImprovement'] as const).map((key) => (
            <label key={key} className="block">
              <span className="form-label">{t(`tools.weeklyReview.fields.${key}`)}</span>
              <textarea
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={t(`tools.weeklyReview.placeholders.${key}`)}
                className="input-field mt-s2 min-h-20"
                data-testid={`weekly-review-${key}`}
                rows={3}
              />
            </label>
          ))}
        </article>

        <article className="rounded-lg border border-flame-ember bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label text-flame-deep">
              {t('tools.weeklyReview.fields.nextWeekFocus')}
            </span>
            <p className="text-xs text-gray-1 mb-s2 italic">
              {t('tools.weeklyReview.hints.nextWeekFocus')}
            </p>
            <textarea
              value={form.nextWeekFocus}
              onChange={(e) => setForm((prev) => ({ ...prev, nextWeekFocus: e.target.value }))}
              placeholder={t('tools.weeklyReview.placeholders.nextWeekFocus')}
              className="input-field mt-s2 min-h-20"
              data-testid="weekly-review-next-week-focus"
              rows={3}
            />
          </label>
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5 space-y-s3">
          <h2 className="font-display text-display-sm text-ink">
            {t('tools.weeklyReview.commitments.title')}
            <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
              {t('tools.weeklyReview.optional')}
            </span>
          </h2>
          {(['energyCommitment', 'workCommitment', 'loveCommitment'] as const).map((key) => (
            <label key={key} className="block">
              <span className="form-label">{t(`tools.weeklyReview.fields.${key}`)}</span>
              <input
                type="text"
                value={form[key]}
                onChange={(e) => setForm((prev) => ({ ...prev, [key]: e.target.value }))}
                placeholder={t(`tools.weeklyReview.placeholders.${key}`)}
                className="input-field mt-s2 w-full"
                data-testid={`weekly-review-${key}`}
              />
            </label>
          ))}
        </article>

        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <label className="block">
            <span className="form-label">
              {t('tools.weeklyReview.fields.rewardEarned')}
              <span className="ml-s2 text-xs font-normal uppercase tracking-wide text-gray-1">
                {t('tools.weeklyReview.optional')}
              </span>
            </span>
            <input
              type="text"
              value={form.rewardEarned}
              onChange={(e) => setForm((prev) => ({ ...prev, rewardEarned: e.target.value }))}
              placeholder={t('tools.weeklyReview.placeholders.rewardEarned')}
              className="input-field mt-s2 w-full"
              data-testid="weekly-review-reward-earned"
            />
          </label>
        </article>

        {submitError && (
          <p role="alert" className="form-error" data-testid="weekly-review-submit-error">
            {submitError}
          </p>
        )}

        {savedAt && (
          <p role="status" className="text-sm text-flame-deep" data-testid="weekly-review-saved-at">
            ✓ {t('tools.weeklyReview.savedAt', { when: savedAt.slice(0, 19).replace('T', ' ') })}
          </p>
        )}

        <FinalizeWarning />

        <button type="submit" className="btn-primary w-full" disabled={!canSubmit}>
          {submitting ? t('tools.weeklyReview.submitting') : t('tools.weeklyReview.submit')}
        </button>
      </form>
    </section>
  );
}
